import { PmBankAccountSearchResponseDto, PricingQuoteDetailsDto, ReadInvoiceDto } from '@inosio/payments-sdk';
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';

type BillingState = {
  isPricingOptionFetching: boolean;
  isPricingOptionSuccess: boolean;
  isPricingOptionError: boolean;
  pricingOptionError: string | null;
  pricingOptionResponse: PricingQuoteDetailsDto | null;
  isAvailableBankAccountFetching: boolean;
  isAvailableBankAccountError: boolean;
  isAvailableBankAccountSuccess: boolean;
  availableBankAccounts: PmBankAccountSearchResponseDto[];
  availableBankAccountError: string | null;
  isUpdateInvoiceBankAccountFetching: boolean;
  isUpdateInvoiceBankAccountError: boolean;
  isUpdateInvoiceBankAccountSuccess: boolean;
  isInvoicesFetching: boolean;
  isInvoicesSuccess: boolean;
  isInvoicesError: boolean;
  invoicesError: string | null;
  invoices: ReadInvoiceDto[];
  isInvoiceFileFetching: boolean;
  isInvoiceFileSuccess: boolean;
  isInvoiceFileError: boolean;
  invoiceFileError: string | null;
};

const getInitialState = (): BillingState => ({
  isPricingOptionFetching: false,
  isPricingOptionSuccess: false,
  isPricingOptionError: false,
  pricingOptionError: null,
  pricingOptionResponse: null,
  isAvailableBankAccountFetching: false,
  isAvailableBankAccountError: false,
  isAvailableBankAccountSuccess: false,
  availableBankAccounts: [],
  availableBankAccountError: null,
  isUpdateInvoiceBankAccountFetching: false,
  isUpdateInvoiceBankAccountError: false,
  isUpdateInvoiceBankAccountSuccess: false,
  isInvoicesFetching: false,
  isInvoicesSuccess: false,
  isInvoicesError: false,
  invoices: [],
  invoicesError: null,
  isInvoiceFileFetching: false,
  isInvoiceFileSuccess: false,
  isInvoiceFileError: false,
  invoiceFileError: null,
});

export const billingSlice = createSlice({
  name: 'billingSlice',
  initialState: getInitialState(),
  reducers: {
    getPricingOption(state, action) {
      state.isPricingOptionFetching = true;
      state.isPricingOptionSuccess = false;
      state.isPricingOptionError = false;
      state.pricingOptionError = null;
    },
    setPricingOptionResponse(state, action) {
      return { ...state, pricingOptionResponse: action.payload, isPricingOptionFetching: false, isPricingOptionSuccess: true };
    },
    setPricingOptionError(state, action) {
      return { ...state, pricingOptionError: action.payload.message, isPricingOptionError: true, isPricingOptionFetching: false };
    },
    getAvailableBankAccount(state, action) {
      state.isAvailableBankAccountFetching = true;
      state.isAvailableBankAccountSuccess = false;
      state.isAvailableBankAccountError = false;
      state.availableBankAccountError = null;
    },
    setAvailableBankAccountsResponse(state, action) {
      return { ...state, availableBankAccounts: action.payload, isAvailableBankAccountFetching: false, isAvailableBankAccountSuccess: true };
    },
    setAvailableBankAccountsError(state, action) {
      return { ...state, availableBankAccountError: action.payload.message, isAvailableBankAccountFetching: false, isAvailableBankAccountError: true };
    },
    getUpdateInvoiceBankAccount(state, action) {
      state.isUpdateInvoiceBankAccountFetching = true;
      state.isUpdateInvoiceBankAccountSuccess = false;
      state.isUpdateInvoiceBankAccountError = false;
    },
    setUpdateInvoiceBankAccountResponse(state, action) {
      return { ...state, isUpdateInvoiceBankAccountSuccess: true, isUpdateInvoiceBankAccountFetching: false };
    },
    setUpdateInvoiceBankAccountError(state, action) {
      return { ...state, isUpdateInvoiceBankAccountError: true, isUpdateInvoiceBankAccountFetching: false };
    },
    getInvoices(state, action) {
      state.isInvoicesFetching = true;
      state.isInvoicesSuccess = false;
      state.isInvoicesError = false;
      state.invoicesError = null;
    },
    setInvoicesResponse(state, action) {
      return { ...state, isInvoicesFetching: false, isInvoiceDetailsSuccess: true, invoices: action.payload };
    },
    setInvoicesError(state, action) {
      return { ...state, isInvoiceDetailsFetching: false, isInvoiceDetailsError: true, invoicesError: action.payload.message };
    },
    getInvoiceFile(state, action) {
      state.isInvoiceFileFetching = true;
      state.isInvoiceFileSuccess = false;
      state.isInvoiceFileError = false;
      state.invoiceFileError = null;
    },
    setInvoiceFileError(state, action) {
      return { ...state, isInvoiceDetailsFetching: false, isInvoiceDetailsError: true, invoiceDetailsError: action.payload.message };
    },
  },
});

export const {
  getPricingOption,
  getAvailableBankAccount,
  getUpdateInvoiceBankAccount,
  setAvailableBankAccountsError,
  setAvailableBankAccountsResponse,
  setUpdateInvoiceBankAccountError,
  setPricingOptionError,
  setPricingOptionResponse,
  setUpdateInvoiceBankAccountResponse,
  getInvoices,
  getInvoiceFile,
  setInvoiceFileError,
  setInvoicesError,
  setInvoicesResponse,
} = billingSlice.actions;

export default billingSlice.reducer;

export const selectBillingState = (root: RootState) => root.billingState;
